import { memo, useEffect, useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import * as Tooltip from "@radix-ui/react-tooltip";
import type { LiveBlock, TokenomicsSummary } from "../../shared/types";
import { formatCoverageBps, formatFlr, formatUsdFromWei } from "../../shared/format";
import { InfoIcon } from "./Icons";
import { StatusPill } from "./StatusPill";
import type { BlockPresentationChannel } from "./blockPresentation";
import {
  buildLiveMetricTotals,
  burnCoverageBps,
  createLiveMetricPresentation,
  netSupplyChangeWei,
  presentLiveMetricTotals,
  rebaseLiveMetricTotals,
} from "./liveMetricTotals";
import flrLogo from "../assets/flr-logo.svg";

gsap.registerPlugin(useGSAP);

function Metric({ label, value, detail, tone, assetIcon=false }: { label: string; value: string; detail: string; tone?: "burn" | "net";assetIcon?:boolean }) {
  return (
    <div className={`burn-metric ${tone ? `burn-metric--${tone}` : ""}`}>
      <span className="eyebrow">{label}</span>
      <strong>{assetIcon&&<img src={flrLogo} alt=""/>}{value}</strong>
      <span>{detail}</span>
    </div>
  );
}

function useAnimatedWei(target: bigint, animationKey: number, animateDecrease = false): bigint {
  const [displayed, setDisplayed] = useState(target);
  const current = useRef(target);
  const previousAnimationKey = useRef(animationKey);
  useEffect(() => {
    const presentedIncrease = animationKey !== previousAnimationKey.current;
    previousAnimationKey.current = animationKey;
    if (target === current.current) return;
    const start = current.current;
    const delta = target - start;
    if (!presentedIncrease || (!animateDecrease && target < start) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      current.current = target;
      setDisplayed(target);
      return;
    }
    const progress = { value: 0 };
    const tween = gsap.to(progress, {
      value: 1,
      duration: 0.58,
      ease: "power3.out",
      overwrite: "auto",
      onUpdate: () => {
        const scaled = BigInt(Math.round(progress.value * 1_000_000));
        const next = start + (delta * scaled) / 1_000_000n;
        current.current = next;
        setDisplayed(next);
      },
      onComplete: () => {
        current.current = target;
        setDisplayed(target);
      },
    });
    return () => { tween.kill(); };
  }, [animateDecrease, animationKey, target]);
  return displayed;
}

function usePresentedLiveMetrics(summary: TokenomicsSummary, presentation: BlockPresentationChannel) {
  const summaryRef = useRef(summary);
  const presentedBlocks = useRef(new Map<string, LiveBlock>());
  const [metrics, setMetrics] = useState(() => createLiveMetricPresentation(buildLiveMetricTotals(summary, [])));

  useEffect(() => presentation.subscribe((block) => {
    presentedBlocks.current.set(block.blockNumber, block);
    const totals = buildLiveMetricTotals(summaryRef.current, [...presentedBlocks.current.values()]);
    setMetrics((current) => presentLiveMetricTotals(current, totals, block.severity));
  }), [presentation]);

  useEffect(() => {
    summaryRef.current = summary;
    const through = BigInt(summary.completeThroughBlock);
    for (const blockNumber of presentedBlocks.current.keys()) {
      if (BigInt(blockNumber) <= through) presentedBlocks.current.delete(blockNumber);
    }
    const totals = buildLiveMetricTotals(summary, [...presentedBlocks.current.values()]);
    setMetrics((current) => rebaseLiveMetricTotals(current, totals));
  }, [summary]);

  return metrics;
}

export const Burnline=memo(function Burnline({ summary, presentation }: { summary: TokenomicsSummary; presentation: BlockPresentationChannel }) {
  const root = useRef<HTMLElement>(null);
  const { totals: target, animationKeys } = usePresentedLiveMetrics(summary, presentation);
  const minted = useAnimatedWei(target.mintedWei, animationKeys.minted);
  const burned = useAnimatedWei(target.burnedWei, animationKeys.burned);
  const authorized = useAnimatedWei(target.authorizedWei, animationKeys.authorized);
  const targetNet = netSupplyChangeWei(target);
  const net = useAnimatedWei(targetNet, animationKeys.net, true);
  const netMagnitude = net < 0n ? -net : net;
  const targetMax = target.mintedWei > target.burnedWei ? target.mintedWei : target.burnedWei;
  const mintHeight = targetMax ? Number((target.mintedWei * 1000n) / targetMax) / 10 : 0;
  const burnHeight = targetMax ? Number((target.burnedWei * 1000n) / targetMax) / 10 : 0;
  const coverage = formatCoverageBps(burnCoverageBps(target.mintedWei, target.burnedWei));
  const price = summary.price?.stale ? null : summary.price?.valueE18 ?? null;

  useGSAP(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    gsap.to(".burnline__signal--mint", { scaleY: mintHeight / 100, duration: reduced ? 0 : 0.62, ease: "power3.out", overwrite: "auto" });
    gsap.to(".burnline__signal--burn", { scaleY: burnHeight / 100, duration: reduced ? 0 : 0.62, ease: "power3.out", overwrite: "auto" });
  }, { scope: root, dependencies: [mintHeight, burnHeight], revertOnUpdate: false });

  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add("(prefers-reduced-motion: no-preference)", () => {
      const pulse=gsap.to(".burnline__zero-pulse", { xPercent: 450, duration: 5.4, ease: "none", repeat: -1, paused: true });
      if(!root.current||typeof IntersectionObserver==="undefined"){
        pulse.play();
        return;
      }
      const observer=new IntersectionObserver(([entry])=>{
        if(entry.isIntersecting)pulse.resume();
        else pulse.pause();
      },{rootMargin:"120px 0px"});
      observer.observe(root.current);
      return()=>observer.disconnect();
    });
    return () => media.revert();
  }, { scope: root });

  const previousBurnPulseKey = useRef(animationKeys.burnPulse);
  useEffect(() => {
    if (animationKeys.burnPulse === previousBurnPulseKey.current) return;
    previousBurnPulseKey.current = animationKeys.burnPulse;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const value = root.current?.querySelector<HTMLElement>(".burn-metric--burn strong");
    if (!value) return;
    const pulse = gsap.timeline();
    pulse.fromTo(value, {
      filter: "brightness(1)",
      textShadow: "0 0 0 rgba(255, 0, 108, 0)",
      scale: 1,
    }, {
      filter: "brightness(1.85)",
      textShadow: "0 0 22px rgba(255, 86, 180, .92)",
      scale: 1.025,
      transformOrigin: "left center",
      duration: 0.22,
      ease: "power2.out",
      repeat: 1,
      yoyo: true,
      clearProps: "filter,textShadow,scale,transformOrigin",
    });
    return () => { pulse.kill(); };
  }, [animationKeys.burnPulse]);

  return (
    <section ref={root} className="burnline" id="tokenomics-state" aria-labelledby="burnline-title">
      <div className="burnline__heading">
        <div className="burnline__title-lockup">
          <span className="eyebrow burnline__kicker">Rolling {summary.range === "30d" ? "30-day" : summary.range} supply observatory</span>
          <h1 id="burnline-title"><span className="burnline__title-mask"><span className="burnline__title-word">The</span></span>{" "}<span className="burnline__title-mask"><span className="burnline__title-word burnline__title-word--accent">Burnline</span></span></h1>
          <p className="burnline__lede">Every confirmed block leaves a trace. Watch issuance enter, permanent burns pull supply away, and Flare’s monetary direction resolve in real time.</p>
        </div>
        <div className="burnline__heading-meta">
          <span className="burnline__live-label"><i />Live monetary instrument</span>
          <StatusPill state={summary.state} />
        </div>
      </div>

      <div className="burnline__stage">
        <div className="burnline__aperture">
          <b className="burnline__orbit burnline__orbit--outer" aria-hidden="true" />
          <b className="burnline__orbit burnline__orbit--inner" aria-hidden="true" />
          <span className="burnline__aperture-caption burnline__aperture-caption--top">Issuance field · FLR entering supply</span>
          <img className="burnline__aperture-mark" src="/flare-burn-monitor-logo.png" alt="" aria-hidden="true" />
          <div className="burnline__instrument" aria-label={`Minted ${formatFlr(minted)} FLR. Burned ${formatFlr(burned)} FLR.`}>
            <div className="burnline__upper">
              <span className="burnline__axis-label">New supply</span>
              <div className="burnline__signal burnline__signal--mint" />
              <span className="burnline__signal-value"><strong>{formatFlr(minted)} FLR</strong><small>minted</small></span>
            </div>
            <div className="burnline__zero" aria-label={`Zero supply axis. Burn coverage ${coverage}.`}>
              <span className="burnline__coverage"><small>Burn coverage</small><strong>{coverage}</strong></span>
              <i><b className="burnline__zero-pulse" /></i>
              <span className="burnline__balance">{net <= 0n ? "Burns lead" : "Issuance leads"} by {formatFlr(netMagnitude)} FLR</span>
            </div>
            <div className="burnline__lower">
              <span className="burnline__axis-label">Removed supply</span>
              <div className="burnline__signal burnline__signal--burn" />
              <span className="burnline__signal-value"><strong>{formatFlr(burned)} FLR</strong><small>burned</small></span>
            </div>
          </div>
          <span className="burnline__aperture-caption burnline__aperture-caption--bottom">Permanent removal field · FLR destroyed</span>
        </div>

        <div className="burnline__metrics">
          <Metric label="Newly minted" value={`${formatFlr(minted)} FLR`} detail={formatUsdFromWei(minted.toString(), price)} assetIcon />
          <Metric label="Permanently burned" value={`${formatFlr(burned)} FLR`} detail={formatUsdFromWei(burned.toString(), price)} tone="burn" assetIcon />
          <Metric
            label="Net supply change"
            value={`${net < 0n ? "−" : "+"}${formatFlr(netMagnitude)} FLR`}
            detail={net < 0n ? "Supply contracted" : "Supply expanded"}
            tone="net"
            assetIcon
          />
          <Metric label="Burn coverage" value={coverage} detail="of realized issuance" tone="burn" />
        </div>
      </div>

      <div className="burnline__context">
        <span>Authorized: <b>{formatFlr(authorized)} FLR</b></span>
        <span>Daily parity gap: <b>{formatFlr(summary.additionalDailyBurnForParityWei)} FLR</b></span>
        <Tooltip.Root>
          <Tooltip.Trigger className="icon-button info-trigger" aria-label="How the net state is decided"><InfoIcon /></Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content className="tooltip" sideOffset={8}>
              Net deflationary requires burns to exceed both actual minting and authorized inflation across a complete window.
              <Tooltip.Arrow className="tooltip__arrow" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </div>
    </section>
  );
});
